import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Loader2, Image as ImgIcon, Link as LinkIcon, Paperclip } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { useNavigate } from "react-router-dom";

async function fileToBase64(file, max = 1400) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const img = new Image();
      img.onload = () => {
        const c = document.createElement("canvas");
        let { width, height } = img;
        if (width > max || height > max) { const s = Math.min(max / width, max / height); width = Math.floor(width * s); height = Math.floor(height * s); }
        c.width = width; c.height = height;
        c.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(c.toDataURL("image/jpeg", 0.82));
      };
      img.onerror = reject; img.src = r.result;
    };
    r.onerror = reject; r.readAsDataURL(file);
  });
}
function linkify(text) {
  if (!text) return null;
  const urlRe = /(https?:\/\/[^\s]+)/g;
  return text.split(urlRe).map((part, i) => urlRe.test(part) ? <a key={i} href={part} target="_blank" rel="noreferrer" className="underline">{part}</a> : part);
}

export default function ChatWidget() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const load = async () => {
    if (!user || user.role === "admin") return;
    try { const { data } = await api.get("/chat/thread"); setMessages(Array.isArray(data.messages) ? data.messages : []); } catch { /* ignore */ }
  };
  useEffect(() => { if (!open) return; load(); const id = setInterval(load, 5000); return () => clearInterval(id); }, [open, user?.user_id]); // eslint-disable-line
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length]);

  if (!user || user.role === "admin") {
    return (
      <button onClick={() => nav("/login?next=/dashboard?tab=chat")} data-testid="chat-widget-login-prompt"
        className="fixed bottom-6 right-6 z-30 h-14 px-5 rounded-full bg-eagle-gold text-eagle-ink font-semibold shadow-lg hover:shadow-xl hover:bg-eagle-goldHover flex items-center gap-2 transition">
        <MessageCircle className="h-5 w-5" /> Chat with us
      </button>
    );
  }

  const send = async () => {
    if (!text.trim() && attachments.length === 0) return;
    setSending(true);
    try { await api.post("/chat/thread/messages", { content: text.trim(), attachments }); setText(""); setAttachments([]); await load(); }
    finally { setSending(false); }
  };
  const onImage = async (file) => { const b64 = await fileToBase64(file); setAttachments([...attachments, { type: "image", url: b64, name: file.name }]); };
  const onLink = () => { const u = window.prompt("Link URL:"); if (u) setAttachments([...attachments, { type: "link", url: u, name: u }]); };

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} data-testid="chat-widget-open"
          className="fixed bottom-6 right-6 z-30 h-14 w-14 rounded-full bg-eagle-gold text-eagle-ink shadow-lg hover:shadow-xl hover:bg-eagle-goldHover flex items-center justify-center transition">
          <MessageCircle className="h-6 w-6" />
        </button>
      )}
      {open && (
        <div className="fixed bottom-6 right-6 z-40 w-[92vw] max-w-sm h-[560px] bg-card border border-border rounded-2xl shadow-2xl flex flex-col overflow-hidden" data-testid="chat-widget">
          <div className="p-4 border-b border-border bg-eagle-ink text-white flex items-center justify-between">
            <div>
              <div className="font-display font-semibold">Live Support</div>
              <div className="text-xs text-white/60">Typically replies within an hour</div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="chat-widget-close"><X className="h-4 w-4" /></button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-background">
            {messages.length === 0 && <div className="text-center text-sm text-muted-foreground py-8">Send us a message — we&apos;ll email you when we reply.</div>}
            {messages.map(m => (
              <div key={m.message_id} className={`flex ${m.sender === "customer" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl text-sm ${m.sender === "customer" ? "bg-eagle-gold text-eagle-ink rounded-br-sm" : "bg-secondary rounded-bl-sm"}`}>
                  {m.attachments?.length > 0 && (
                    <div className={`p-2 space-y-2 ${m.content ? "border-b border-black/10" : ""}`}>
                      {m.attachments.map((a, i) => a.type === "image" ? (
                        <a key={i} href={a.url} target="_blank" rel="noreferrer"><img src={a.url} alt="" className="rounded-lg max-w-full max-h-48 object-cover" /></a>
                      ) : (
                        <a key={i} href={a.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 px-2 py-1 rounded bg-black/10"><LinkIcon className="h-3 w-3" /><span className="text-xs truncate">{a.name}</span></a>
                      ))}
                    </div>
                  )}
                  {m.content && <div className="px-3.5 py-2 whitespace-pre-wrap">{linkify(m.content)}</div>}
                </div>
              </div>
            ))}
            <div ref={endRef} />
          </div>
          {attachments.length > 0 && (
            <div className="px-3 pt-2 flex flex-wrap gap-2 border-t border-border">
              {attachments.map((a, i) => (
                <div key={i} className="relative">
                  {a.type === "image" ? <img src={a.url} alt="" className="h-12 w-12 rounded object-cover" /> : <div className="h-12 px-3 rounded bg-secondary text-xs flex items-center gap-1"><LinkIcon className="h-3 w-3" />link</div>}
                  <button onClick={() => setAttachments(attachments.filter((_, j) => j !== i))} className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 text-white flex items-center justify-center"><X className="h-3 w-3" /></button>
                </div>
              ))}
            </div>
          )}
          <div className="p-3 border-t border-border flex items-end gap-1">
            <label className="cursor-pointer">
              <Button variant="ghost" size="sm" type="button" asChild data-testid="widget-attach-image"><span><ImgIcon className="h-4 w-4" /></span></Button>
              <input type="file" accept="image/*" hidden onChange={(e) => e.target.files?.[0] && onImage(e.target.files[0])} />
            </label>
            <Button variant="ghost" size="sm" onClick={onLink} data-testid="widget-attach-link"><LinkIcon className="h-4 w-4" /></Button>
            <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Type your message…" rows={2} data-testid="chat-widget-input" className="resize-none" onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} />
            <Button onClick={send} disabled={sending || (!text.trim() && attachments.length === 0)} data-testid="chat-widget-send" className="bg-eagle-gold text-eagle-ink hover:bg-eagle-goldHover">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      )}
    </>
  );
}